/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2018 Adobe
 *  All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/
import React, { Component, ReactElement } from 'react';
import { ConnectedProps, connect } from 'react-redux';
import { WrappedComponentProps, injectIntl } from 'react-intl';
import ReactDOM from 'react-dom';
import {
	fetchSearchHistoryItems,
	setExactTypedSearchQuery,
	setFilteredSearchDropdownItems,
	setSearchHistoryItems,
	setSearchQuery,
	setSourceOfQuery,
	setSubmittedSearchQuery,
	setTempSearchQuery,
	showSearchDropdown,
} from '../../../actions/searchActions';
import SearchUtils from '../../../utils/SearchUtils';
import Clock from '@react/react-spectrum/Icon/Clock';
import Close from '@react/react-spectrum/Icon/Close';
import SearchService from '../../../services/SearchService';
import SearchAnalytics from '../../../analytics/SearchAnalytics';
import './SearchDropdownHistoryItem.scss';
import { SourceOfQuery } from '../../../containers/SearchResultsPage/SearchConstants';
import SearchHostContext from '../../../utils/SearchHostContext';
type OwnProps = {
	searchHostContext: HostContext;
	logger: Logger;
	onSubmit: (...args: Array<any>) => any;
	item: string;
	index: number;
	intl: any;
	searchQuery: string;
	tempSearchQuery?: string;
	hostShowSearchDropdown: boolean;
};

type SearchHistoryItemProps = OwnProps & PropsFromRedux & WrappedComponentProps;
/*
 * SearchHistoryItem for Search.
 */

class SearchHistoryItem extends Component<SearchHistoryItemProps> {
	closeButton: any;

	/*
	 * Get the searchData to store to local storage
	 */
	getSearchData(item: string) {
		SearchAnalytics.keyword = item;
		return {
			prompt: SearchAnalytics.prompt,
			keyword: SearchAnalytics.keyword,
		};
	}

	/*
	 * Check if the element is the close button
	 * @return <boolean>
	 */
	isCloseButton(event: Record<string, any>) {
		let isCloseButton = false;

		if (this.closeButton && this.closeButton.contains(event.target)) {
			isCloseButton = true;
		}

		return isCloseButton;
	}

	/*
	 * Updates the Search History Item
	 */
	async updateSearchHistoryItem(index: number, item: string) {
		const trimmedSearchQuery = item.trim();
		const searchHistoryItems = [...this.props.searchHistoryItems];

		if (searchHistoryItems && Array.isArray(searchHistoryItems) && searchHistoryItems.length > 0) {
			const updatedSearchHistoryItem = searchHistoryItems.splice(index, 1);
			searchHistoryItems.unshift(updatedSearchHistoryItem[0]);
			this.props.setSearchHistoryItems(searchHistoryItems);
			SearchUtils.setSearchHistoryWithSearchQuery(searchHistoryItems, trimmedSearchQuery);

			try {
				const authenticated = SearchHostContext.isAuthenticated();
				if (
					authenticated &&
					this.props.searchHostContext.featureFlags.includes('temp_enable_search_history_server')
				) {
					if (trimmedSearchQuery !== '') {
						const searchService = new SearchService(this.props.searchHostContext);
						await searchService.pushSearchHistory(trimmedSearchQuery);
					}
				}
			} catch (e) {
				this.props.logger.error(
					'Search History Item',
					'Problem creating/updating search history to search server on search history item click',
					e,
				);
			}
		}
	}

	/*
	 * Deletes the Search History Item from local storage and search server
	 */
	async deleteSearchHistoryItemFromLocalStorageAndServer(filteredIndex: number, item: string) {
		const searchHistoryItems = this.props.searchHistoryItems;
		const filteredSearchHistoryItems = this.props.filteredSearchDropdownItems.searchHistory;

		if (
			filteredSearchHistoryItems &&
			Array.isArray(filteredSearchHistoryItems) &&
			filteredSearchHistoryItems.length > 0
		) {
			if (searchHistoryItems && Array.isArray(searchHistoryItems) && searchHistoryItems.length > 0) {
				// @ts-ignore
				const index = searchHistoryItems.indexOf(filteredSearchHistoryItems[filteredIndex]);
				searchHistoryItems.splice(index, 1);
				this.props.setSearchHistoryItems(searchHistoryItems);
				SearchUtils.setHistoryDataToSessionStorage(searchHistoryItems);

				try {
					const authenticated = SearchHostContext.isAuthenticated();

					if (
						authenticated &&
						this.props.searchHostContext.featureFlags.includes('temp_enable_search_history_server')
					) {
						const searchService = new SearchService(this.props.searchHostContext);
						await searchService.deleteSearchHistory(item);
					}

					this.props.fetchSearchHistoryItems(this.props.searchHostContext);
				} catch (e) {
					this.props.logger.error(
						'Search History Item',
						'Problem deleting search history on search server on clicking remove',
						e,
					);
				}
			}
		}
	}

	/*
	 * Deletes the Search History Item from the UI
	 */
	deleteSearchHistoryItem(index: number, item: string, event: Record<string, any>) {
		const analytics = new SearchAnalytics(this.props.searchHostContext);
		analytics.postDeleteSearchHistoryIem(index, item);

		if (this.isCloseButton(event)) {
			this.deleteSearchHistoryItemFromLocalStorageAndServer(index, item);
			const filteredResults = { ...this.props.filteredSearchDropdownItems };
			filteredResults.searchHistory = SearchUtils.getFilteredSearchHistoryItems(this.props.searchQueryValue, [
				...this.props.searchHistoryItems,
			]);
			const showDropdown = SearchUtils.shouldShowSearchDropdownItems(
				this.props.searchQueryValue,
				filteredResults,
			);
			// Close the Search Dropdown when no items to display and stop propogation of event if the close button is clicked
			if (showDropdown !== this.props.showSearchDropdownValue) {
				this.props.showSearchDropdown(this.props.hostShowSearchDropdown, showDropdown);
			} else {
				event.stopPropagation();
			}
		}
	}

	/*
	 * Executes the Search History Item Search Query
	 */
	executeQuery(index: number, item: string, exactTypedSearchQuery: string, event: Record<string, any>) {
		this.props.setSourceOfQuery(SourceOfQuery.History);
		SearchAnalytics.searchType = SearchAnalytics.history;
		const analytics = new SearchAnalytics(this.props.searchHostContext);
		analytics.initSearchAnalytics(item);
		analytics.postDropDownSelection(index, exactTypedSearchQuery);

		if (!this.isCloseButton(event)) {
			this.updateSearchHistoryItem(index, item);
			this.props.setTempSearchQuery('');
			this.props.setExactTypedSearchQuery(exactTypedSearchQuery);
			this.props.setSearchQuery(item);
			this.props.showSearchDropdown(this.props.hostShowSearchDropdown, false);
			this.props.setSubmittedSearchQuery(true);
			// Save to local storage in case the refresh the results page
			this.props.onSubmit(item);
			this.props.logger.info('Search History', 'Logged Search Query', item);
		}
	}

	/*
	 * Returns the search query elements with bold charecters
	 * @return <element>
	 */
	getSearchQueryBold() {
		let searchQuery = this.props.searchQueryValue;
		searchQuery = searchQuery.toLocaleLowerCase();
		const textArray = SearchUtils.getSearchQueryItemSplitArray(this.props.item, searchQuery);
		return (
			<span className='search-dropdown-text-bold'>
				{textArray.map((item) => (
					<>{item === searchQuery ? <b>{item}</b> : item}</>
				))}
			</span>
		);
	}

	render() {
		return (
			<div
				className='search-dropdown-history-item'
				id={`search-dropdown-history-item-${this.props.index}`}
				tabIndex={-1}
				data-ccs-query={this.props.item}
				onClick={(event) => this.executeQuery(this.props.index, this.props.item, this.props.searchQuery, event)}
			>
				<div className='search-dropdown-history-item-text-container'>
					<div className='search-dropdown-history-item-history-icon-container'>
						<Clock className='search-dropdown-history-item-history-icon' />
					</div>
					<div className='search-dropdown-history-item-text'>{this.getSearchQueryBold()}</div>
				</div>
				<div
					className='search-dropdown-history-item-close-icon-container' // FIXME: replace with ref if possible
					// eslint-disable-next-line react/no-find-dom-node
					ref={(component) => {
						this.closeButton = ReactDOM.findDOMNode(component);
					}}
					onClick={(event) => this.deleteSearchHistoryItem(this.props.index, this.props.item, event)}
				>
					<Close className='search-dropdown-history-item-close-icon' />
				</div>
			</div>
		);
	}
}

const mapStateToProps = ({ search }: RootState) => ({
	searchHistoryItems: search.searchHistoryItems,
	filteredSearchDropdownItems: search.filteredSearchDropdownItems,
	searchQueryValue: search.searchQuery,
	showSearchDropdownValue: search.showSearchDropdown,
});

const mapDispatchToProps = {
	setFilteredSearchDropdownItems,
	setSubmittedSearchQuery,
	setSearchHistoryItems,
	setSearchQuery,
	setExactTypedSearchQuery,
	setTempSearchQuery,
	showSearchDropdown,
	fetchSearchHistoryItems,
	setSourceOfQuery,
};
const connector = connect(mapStateToProps, mapDispatchToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;

export default connector(injectIntl(SearchHistoryItem));
